// src/utils/googleTagManager.js
import {
  CURRENCY_CODE,
  DATA_LAYER_NAME,
  GOOGLE_GTMIDS,
  PRODUCT_CATEGORY,
  PRODUCT_VARIANT,
  USER_CENTRICS_GTM_CATEGORY,
} from '../constants';
import priceUtils from './priceUtils';

// --- debug helpers (keeps console noise readable) ---
const LOG_PREFIX = '[UC→GTM]';

/* eslint-disable no-console -- deliberate, prefixed GTM debug channel */
function log(...args) {
  if (typeof console !== 'undefined' && typeof console.log === 'function') {
    console.log(LOG_PREFIX, ...args);
  }
}
/* eslint-enable no-console */

function warn(...args) {
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(LOG_PREFIX, ...args);
  }
}

let _lastConsentDecision = null; // memoize last UC decision we logged
let _ucReadyLogged = false; // ensure we log UC readiness only once
let _pollLoggedOnce = false; // avoid spamming during polling

/**
 * Best-effort nonce discovery.
 * We want to propagate the CSP nonce to the dynamically-created GTM <script> tag
 * so GTM can apply the same nonce to any injected inline scripts it creates.
 */
function getCspNonce() {
  if (typeof document === 'undefined') return null;

  // 1) Prefer a real nonce attribute in the DOM
  const el = document.querySelector('script[nonce]') || document.querySelector('[nonce]');
  const domNonce = (el && (el.nonce || el.getAttribute('nonce'))) || null;
  if (domNonce) return domNonce;

  // 2) Fallback: parse nonce from CSP meta tag
  const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  const content = cspMeta ? cspMeta.getAttribute('content') || '' : '';
  const match = content.match(/'nonce-([^']+)'/);
  return match ? match[1] : null;
}

/** Normalise UC service/category API shapes to arrays */
function normaliseUcCollection(result) {
  if (Array.isArray(result)) return result;
  if (result && Array.isArray(result.services)) return result.services;
  if (result && Array.isArray(result.data)) return result.data;
  if (result && Array.isArray(result.categories)) return result.categories;
  return [];
}

function hasGrantedStatus(st) {
  if (st === true) return true;
  if (typeof st === 'string') {
    const value = st.toLowerCase();
    return value === 'active' || value === 'accepted' || value === 'granted' || value === 'true';
  }
  return false;
}

/** Load GTM once */
function loadGTM(brand) {
  if (typeof window === 'undefined') return;
  if (window.__gtmLoaded) {
    log('loadGTM(): already loaded; skipping');
    return;
  }

  // GTM containers can carry third-party consent-management tags that render their own UI
  // once the container loads. Quality-gate CI serves the real stage build from localhost, so
  // without this guard those tags fire there too, blocking automated interactions the tests
  // never account for.
  if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
    log("loadGTM(): localhost detected – GTM skipped to prevent consent-tag overlays in CI/dev");
    return;
  }

  window.__gtmLoaded = true;

  const gtmId = GOOGLE_GTMIDS[brand];
  log('loadGTM(): injecting GTM', { brand, gtmId });

  const dl = (window[DATA_LAYER_NAME] = window[DATA_LAYER_NAME] || []);
  const originalLocation =
    window.location.protocol +
    '//' +
    window.location.hostname +
    window.location.pathname +
    window.location.search;

  dl.push({
    originalLocation,
  });
  log('dataLayer seeded', { DATA_LAYER_NAME, originalLocation });

  (function init(w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dlParam = l !== 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dlParam;

    // ✅ CSP nonce propagation (fixes "unsafe-inline ignored due to nonce" blocks)
    const nonce = getCspNonce();
    if (nonce) {
      // Set both attribute + property for maximum browser compatibility
      j.setAttribute('nonce', nonce);
      j.nonce = nonce;
      log('Applied CSP nonce to GTM script tag', { noncePresent: true });
    } else {
      warn('No CSP nonce found in DOM; GTM may be blocked under nonce-based CSP', {
        noncePresent: false,
      });
    }

    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', DATA_LAYER_NAME, gtmId);

  // Evidence that GTM actually loaded
  window.setTimeout(() => {
    const perf = window.performance?.getEntriesByName
      ? window.performance.getEntriesByName(`https://www.googletagmanager.com/gtm.js?id=${gtmId}`)
      : [];
    const gtmObjPresent = !!window.google_tag_manager;
    log('post-load check', {
      gtmObjPresent,
      perfRequests: perf && perf.length ? perf.length : 0,
    });
  }, 800);
}

/** UC helpers (browser-safe) */
function onUcReady(cb) {
  if (typeof window === 'undefined') return;
  const UC = window.UC_UI;
  if (UC && (UC.getServicesBaseInfo || UC.getCategoriesBaseInfo)) {
    if (!_ucReadyLogged) {
      log('UC ready immediately', {
        hasServicesAPI: !!UC.getServicesBaseInfo,
        hasCategoriesAPI: !!UC.getCategoriesBaseInfo,
      });
      _ucReadyLogged = true;
    }
    return cb();
  }

  if (!_ucReadyLogged) {
    log('Waiting for UC runtime...');
    _ucReadyLogged = true;
  }

  let tries = 0;
  const t = window.setInterval(() => {
    tries++;
    const UCnow = window.UC_UI;
    if (UCnow && (UCnow.getServicesBaseInfo || UCnow.getCategoriesBaseInfo)) {
      window.clearInterval(t);
      log('UC became ready', {
        afterMs: tries * 100,
        hasServicesAPI: !!UCnow.getServicesBaseInfo,
        hasCategoriesAPI: !!UCnow.getCategoriesBaseInfo,
      });
      cb();
    }
    if (tries > 120) {
      window.clearInterval(t);
      warn('UC not ready after ~12s – consent gating may rely on later events/polling');
    }
  }, 100);
}

async function consentAllowsGTM() {
  if (typeof window === 'undefined') return false;
  const UC = window.UC_UI;
  let decision = false;
  let method = 'none';
  let detail = {};

  try {
    // Prefer service-level check, using the same analytics-style matching as the UC -> GCM bridge.
    if (UC && typeof UC.getServicesBaseInfo === 'function') {
      method = 'services';
      const svcs = normaliseUcCollection(await UC.getServicesBaseInfo());

      const analyticsService = svcs.find((s) => {
        const name = String(s?.name || '');
        const cat = String(s?.categorySlug || '');
        const ok = hasGrantedStatus(s && s.consent && s.consent.status);

        return (
          ok &&
          (/(google analytics( 4)?|ga4|google tag(?! manager))/i.test(name) ||
            /(stat|analyt|measure|performance|usage|customcategory)/i.test(cat))
        );
      });

      decision = !!analyticsService;
      detail = {
        foundService: !!analyticsService,
        matchedServiceName: analyticsService ? analyticsService.name : undefined,
        matchedCategory: analyticsService ? analyticsService.categorySlug : undefined,
        serviceConsent: analyticsService?.consent ? analyticsService.consent.status : undefined,
        servicesCount: svcs.length,
      };
    } else if (UC && typeof UC.getCategoriesBaseInfo === 'function') {
      // Fallback: category slug (default "statistics")
      method = 'categories';
      const cats = normaliseUcCollection(await UC.getCategoriesBaseInfo());
      const required = USER_CENTRICS_GTM_CATEGORY || 'statistics';
      const c = cats.find((x) => x.slug === required);
      decision = !!(c && c.isActive);
      detail = {
        category: required,
        active: !!(c && c.isActive),
        categoriesCount: cats.length,
      };
    }
  } catch (e) {
    method = method + '+error';
    detail = { error: String(e && e.message ? e.message : e) };
  }

  // Only log if the decision or method changed (avoid spam during polling)
  const signature = JSON.stringify({ decision, method, detail });
  if (_lastConsentDecision !== signature) {
    _lastConsentDecision = signature;
    log('consentAllowsGTM()', { method, decision, detail });
  }

  return decision;
}

/** Public: init GTM, gated by UC when enableUsercentrics is true */
export const initGTM = (brand, enableUsercentrics = false) => {
  if (typeof window === 'undefined') return;

  log('initGTM()', { brand, enableUsercentrics, gtmId: GOOGLE_GTMIDS[brand] });

  if (enableUsercentrics) {
    onUcReady(async () => {
      const allowed = await consentAllowsGTM();
      log('UC ready → consent check', { allowed });
      if (allowed) {
        loadGTM(brand);
      } else {
        log('GTM blocked (no consent yet)');
      }
    });

    window.addEventListener('ucConsent', async (e) => {
      log('ucConsent event', { detail: e?.detail });
      if (await consentAllowsGTM()) {
        loadGTM(brand);
      } else {
        log('ucConsent fired but consent still not sufficient; GTM blocked');
      }
    });

    // Fallback polling in case no UC event is fired
    let checks = 0;
    const poll = window.setInterval(async () => {
      if (!_pollLoggedOnce) {
        _pollLoggedOnce = true;
        log('Starting consent polling fallback (max ~12s)...');
      }
      if (await consentAllowsGTM()) {
        window.clearInterval(poll);
        log('Polling detected consent → loading GTM');
        loadGTM(brand);
      }
      if (++checks > 120) {
        window.clearInterval(poll);
        log('Consent polling finished without approval – GTM not loaded');
      }
    }, 100);
  } else {
    // UC disabled: load GTM immediately (brand's own cookie solution applies)
    log('UC disabled → loading GTM immediately');
    loadGTM(brand);
  }
};

export const trackEvent = ({ brand, eventName, payload, railcardId, step }) => {
  if (typeof window === 'undefined') return;
  const dataLayer = window[DATA_LAYER_NAME];

  if (!dataLayer) {
    warn('trackEvent(): dataLayer not ready – event skipped', { eventName });
    return;
  }

  const { id, name, price } = payload;

  const dataLayerObject = {
    event: `OTRL:${eventName}`,
    ...(step.stepNumber !== 0 && {
      ecommerce: {
        checkout: {
          id: railcardId, // DRC order ID (may be empty)
          actionField: {
            step: step.stepNumber,
            option: step.stepName,
          },
        },
        currencyCode: CURRENCY_CODE,
        add: {
          products: [
            {
              id,
              name,
              price,
              quantity: 1,
              variant: PRODUCT_VARIANT,
              brand,
              category: PRODUCT_CATEGORY,
              dimension6: 'false',
              dimension7: 'false',
              dimension8: 'false',
              dimension9: false,
              dimension10: false,
              dimension11: false,
              dimension12: false,
              dimension13: false,
              dimension14: 'false',
              dimension15: 'false',
              dimension16: false,
              dimension17: 'false',
              dimension18: 'false',
              dimension19: 'none',
            },
          ],
        },
      },
    }),
  };

  log('trackEvent(): pushing to dataLayer', { eventName, payload: { id, name, price }, step });
  dataLayer.push(dataLayerObject);
};

/** Keep original helper intact */
export const getRailcardEventPayload = ({ railcard, chosenPlan }) => {
  const { railcardCode, railcardName, oneYearPrice, originalPrice, threeYearPrice } = railcard;
  const pencePrice = (chosenPlan === 1 ? oneYearPrice : threeYearPrice) || originalPrice;
  const price = priceUtils.formatPriceToPounds(pencePrice);
  const name = `${railcardName} ${chosenPlan} ${chosenPlan === 1 ? 'year' : 'years'}`;

  return {
    id: `${railcardCode}-${chosenPlan}`,
    name,
    price,
  };
};

/** Optional: simple check */
export const hasGTMLoaded = () => typeof window !== 'undefined' && !!window.google_tag_manager;
